/**
 * ARKORBIT UI-Tokens — Portal, Donna, Website (OnlyOffice unberührt).
 * PORTAL_LESESTUFE: berechnete Schrift liegt an `html[data-portal-root]` in index.css
 * (html 90% → Fließtext 1rem / 14.4px, Nebenzeile max(0.875rem, 13px)).
 * LEX/JUD-Stempel ausserhalb der Sidebar 16px fest.
 * Sidebar-Footer (.portal-sidebar-footer) 13px, eine Zeile.
 * Diese Klassen nicht pro Fläche unter 13px ziehen. Chat-Wurzel bleibt 90%.
 */
export const ARKORBIT_THEME_COLORS = {
  canvas: '#070907',
  background: '#0d0f12',
  surface: '#141715',
  toolbar: '#1a1d20',
  accent: '#8b5cf6',
  accentLight: '#a78bfa',
  accentHover: '#7c3aed',
  pixel: '#8b5cf6',
  border: 'rgba(255, 255, 255, 0.06)',
  textPrimary: '#e6e7ea',
  textMuted: '#94a3b8',
} as const;

/** ARKO-Composer / Hub — charcoal + violet accent (ARKORBIT Portal). */
export const CURSOR_PORTAL = {
  canvas: '#07070a',
  rail: '#0d0f12',
  surface: '#141619',
  border: 'rgba(255, 255, 255, 0.08)',
  borderSubtle: 'rgba(255, 255, 255, 0.06)',
  accent: '#8b5cf6',
  accentHover: '#a78bfa',
  accentMuted: '#6d28d9',
  textPrimary: '#e6e7ea',
  textSecondary: '#94a3b8',
  textHint: '#64748b',
  composer:
    'rounded-xl border border-white/[0.08] bg-[#141619] shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_8px_32px_rgba(0,0,0,0.35)]',
  composerFocus: 'focus-within:border-violet-500/35 focus-within:shadow-[0_0_0_1px_rgba(139,92,246,0.15)]',
  chip:
    'rounded-lg border border-white/[0.08] bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-violet-500/25 transition-colors',
  chipActive: 'border-violet-500/40 bg-violet-500/10 text-white ring-1 ring-violet-400/25',
  railBtn:
    'w-full flex items-center justify-center gap-2 rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-sm text-slate-200 hover:border-violet-500/30 hover:bg-violet-500/5 transition-colors',
  threadActive: 'bg-violet-500/10 text-white border-l-2 border-violet-400',
  threadIdle: 'text-slate-500 hover:bg-white/[0.03] hover:text-slate-200',
} as const;

export const CURSOR_CHAT = {
  shell: 'bg-transparent border-0 rounded-none flex flex-col overflow-hidden h-full min-h-0',
  header: 'border-b border-white/[0.06] bg-slate-900/80 flex-shrink-0',
  inputBar: 'bg-transparent border-0 flex-shrink-0',
  input:
    'flex-1 bg-transparent border-0 text-[#e6e7ea] text-[15px] focus:outline-none placeholder:text-slate-600 resize-none overflow-hidden leading-relaxed',
  botBubble: 'bg-[#141619] text-slate-300 border border-white/[0.06] rounded-2xl rounded-tl-sm',
  userBubble: 'bg-violet-600 text-white border border-violet-500/30 rounded-2xl rounded-tr-sm',
  typingBubble: 'bg-[#141619] border border-white/[0.06] rounded-2xl rounded-tl-sm',
  sendBtn:
    'bg-violet-600/20 hover:bg-violet-600/30 text-violet-200 border border-violet-500/35 disabled:opacity-40 transition-colors',
} as const;

/** Dezente Portal-Ränder — Sidebar, Chat, Split-Panes. */
export const PORTAL_BORDER = {
  subtle: 'border-white/[0.06]',
  default: 'border-white/[0.08]',
} as const;

/** Sidebar-Navigation — auch Referenz für aktive Chat-Tabs/Chips. */
export const PORTAL_NAV = {
  item: 'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors',
  active:
    'bg-violet-500/15 text-white ring-1 ring-violet-400/30 shadow-[0_0_16px_rgba(167,139,250,0.12)] border-l-2 border-violet-400/60',
  idle: 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.04]',
} as const;

/** Portal-Buttons — Primary = Sidebar-Aktiv / Senden; Secondary = Slate-Chrome. */
export const PORTAL_BTN = {
  primary:
    'bg-violet-600 hover:bg-violet-500 text-white ring-1 ring-violet-400/30 shadow-[0_0_12px_rgba(167,139,250,0.22)] transition-colors',
  primaryDisabled: 'bg-slate-800 text-slate-500 cursor-not-allowed',
  secondary:
    'bg-slate-900/60 border border-white/[0.06] text-slate-400 hover:bg-slate-800/80 hover:text-slate-200 transition-colors',
  secondaryDisabled: 'bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed',
} as const;

/** Chat-Titel in der Leiste — Text, kein Eingabefeld. */
export const PORTAL_TAB = {
  base: 'flex items-center gap-1.5 border border-transparent bg-transparent transition-colors cursor-pointer min-w-0',
  active: 'bg-transparent border-transparent border-b-2 border-b-violet-400 text-white font-semibold rounded-none shadow-none ring-0',
  idle: 'bg-transparent border-transparent text-slate-500 hover:text-slate-200 rounded-none',
  iconActive: 'text-violet-300',
  iconIdle: 'text-slate-500',
} as const;

/** ARKO-Chat-Fläche — Bubbles, Input, Archive (ohne Donna). */
export const PORTAL_CHAT = {
  shell: 'bg-[#0d0f12]/60 border border-white/[0.06] rounded-xl flex flex-col overflow-hidden h-full',
  shellInner: 'bg-transparent border-0 shadow-none rounded-none min-h-0',
  header: 'border-b border-white/[0.06] bg-slate-900/60 flex-shrink-0',
  inputBar: 'bg-slate-950/50 border-t border-white/[0.06] flex-shrink-0',
  input:
    'flex-1 bg-[#141619]/90 border border-white/[0.08] rounded-xl text-[#e6e7ea] text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/40 placeholder:text-slate-600 resize-none overflow-hidden leading-relaxed transition-colors',
  botBubble: 'bg-[#141619]/90 text-slate-300 border border-white/[0.06] rounded-2xl rounded-tl-none',
  userBubble: 'bg-violet-600 text-white rounded-2xl rounded-tr-none ring-1 ring-violet-400/20',
  typingBubble: 'bg-slate-800/90 border border-white/[0.06] rounded-2xl rounded-tl-none',
  archivePanel: 'bg-slate-900/60 border border-white/[0.06] rounded-xl',
  contextBar: 'rounded-lg border border-white/[0.06] bg-slate-900/60',
  toggleOn: 'bg-violet-600',
  toggleOff: 'bg-slate-700',
  /** Schmale Split-Toolbar: eine Zeile, max. ~36px sichtbare Höhe. */
  compactToolbar:
    'flex items-center gap-1 min-h-[32px] max-h-[36px] overflow-x-auto custom-scrollbar px-0.5 py-0 border-b border-white/[0.06]',
  compactToolbarDivider: 'w-px h-3.5 bg-white/[0.08] flex-shrink-0',
  /** Schmale Statuszeile unter ARKO-Toolbar (Intent + Dokument-Kontext). */
  statusBar:
    'flex items-center gap-2 flex-wrap min-h-[22px] px-1.5 py-0.5 text-[10px] text-slate-500 border-b border-white/[0.04] bg-slate-950/30',
} as const;

/** Ein/Aus-Schalter (Kontext, Features) — nicht Pin/Merken-Chips. */
export const PORTAL_SWITCH = {
  trackOn: {
    violet: PORTAL_CHAT.toggleOn,
    sky: 'bg-sky-500',
    emerald: 'bg-emerald-600',
  },
  trackOff: PORTAL_CHAT.toggleOff,
  thumb: 'inline-block rounded-full bg-white shadow transition-transform',
} as const;

export type ChatAccentColor = 'blue' | 'purple' | 'amber' | 'cursor';

/** InlineChat-Akzente — abgeleitet von PORTAL_BTN / UI_ACCENT blue. */
export const CHAT_ACCENT: Record<
  ChatAccentColor,
  { icon: string; iconColor: string; dot: string; btn: string; userBubble: string; qa: string }
> = {
  blue: {
    icon: 'bg-blue-500/15 border border-blue-500/30',
    iconColor: 'text-blue-300',
    dot: 'bg-blue-500',
    btn: PORTAL_BTN.primary,
    userBubble: PORTAL_CHAT.userBubble,
    qa: 'bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/25 text-blue-200 ring-1 ring-blue-400/20',
  },
  purple: {
    icon: 'bg-purple-500/15 border border-purple-500/30',
    iconColor: 'text-purple-300',
    dot: 'bg-purple-500',
    btn: 'bg-purple-600 hover:bg-purple-500 text-white ring-1 ring-purple-400/30 transition-colors',
    userBubble: 'bg-purple-600 text-white rounded-2xl rounded-tr-none ring-1 ring-purple-400/20',
    qa: 'bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/25 text-purple-200',
  },
  amber: {
    icon: 'bg-amber-500/15 border border-amber-500/30',
    iconColor: 'text-amber-300',
    dot: 'bg-amber-500',
    btn: 'bg-amber-600 hover:bg-amber-500 text-white ring-1 ring-amber-400/30 transition-colors',
    userBubble: 'bg-amber-600 text-white rounded-2xl rounded-tr-none ring-1 ring-amber-400/20',
    qa: 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/25 text-amber-200',
  },
  cursor: {
    icon: 'bg-violet-500/10 border border-violet-500/25',
    iconColor: 'text-violet-300',
    dot: 'bg-violet-500',
    btn: CURSOR_CHAT.sendBtn,
    userBubble: CURSOR_CHAT.userBubble,
    qa: CURSOR_PORTAL.chip,
  },
};

export type UiAccent = 'blue' | 'cyan' | 'violet' | 'emerald' | 'amber' | 'cursor';

export const UI_ACCENT: Record<
  UiAccent,
  { chip: string; chipActive: string; badge: string; badgeGlow: string; pixel: string }
> = {
  blue: {
    chip: 'border-blue-500/20 bg-blue-500/5 text-blue-300/90 hover:bg-blue-500/10 hover:border-blue-500/30',
    chipActive:
      'border-blue-500/50 bg-blue-500/25 text-white ring-2 ring-blue-400/45 shadow-[0_0_16px_rgba(37,99,235,0.22)]',
    badge: 'border-blue-500/30 text-blue-300 bg-blue-500/10',
    badgeGlow: 'shadow-[0_0_24px_rgba(37,99,235,0.35)] border-blue-400/40',
    pixel: '#3b82f6',
  },
  cyan: {
    chip: 'border-cyan-500/20 bg-cyan-500/5 text-cyan-200/90 hover:bg-cyan-500/10 hover:border-cyan-500/30',
    chipActive:
      'border-cyan-500/50 bg-cyan-500/25 text-white ring-2 ring-cyan-400/45 shadow-[0_0_16px_rgba(34,211,238,0.22)]',
    badge: 'border-cyan-500/30 text-cyan-200 bg-cyan-500/10',
    badgeGlow: 'shadow-[0_0_24px_rgba(34,211,238,0.35)] border-cyan-400/40',
    pixel: '#22d3ee',
  },
  violet: {
    chip: 'border-violet-500/20 bg-violet-500/5 text-violet-200/90 hover:bg-violet-500/10 hover:border-violet-500/30',
    chipActive:
      'border-violet-500/50 bg-violet-500/25 text-white ring-2 ring-violet-400/45 shadow-[0_0_16px_rgba(139,92,246,0.22)]',
    badge: 'border-violet-500/30 text-violet-200 bg-violet-500/10',
    badgeGlow: 'shadow-[0_0_24px_rgba(139,92,246,0.35)] border-violet-400/40',
    pixel: '#a78bfa',
  },
  emerald: {
    chip: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-200/90 hover:bg-emerald-500/10 hover:border-emerald-500/30',
    chipActive:
      'border-emerald-500/50 bg-emerald-500/25 text-white ring-2 ring-emerald-400/45 shadow-[0_0_16px_rgba(16,185,129,0.22)]',
    badge: 'border-emerald-500/30 text-emerald-200 bg-emerald-500/10',
    badgeGlow: 'shadow-[0_0_24px_rgba(16,185,129,0.35)] border-emerald-400/40',
    pixel: '#34d399',
  },
  amber: {
    chip: 'border-amber-500/20 bg-amber-500/5 text-amber-100/90 hover:bg-amber-500/10 hover:border-amber-500/30',
    chipActive:
      'border-amber-500/50 bg-amber-500/25 text-white ring-2 ring-amber-400/45 shadow-[0_0_16px_rgba(245,158,11,0.22)]',
    badge: 'border-amber-500/30 text-amber-200 bg-amber-500/10',
    badgeGlow: 'shadow-[0_0_24px_rgba(245,158,11,0.35)] border-amber-400/40',
    pixel: '#fbbf24',
  },
  cursor: {
    chip: 'border-white/[0.08] bg-slate-900/60 text-slate-400 hover:bg-violet-500/5 hover:border-violet-500/25 hover:text-slate-200',
    chipActive:
      'border-violet-500/40 bg-violet-500/10 text-white ring-1 ring-violet-400/25',
    badge: 'border-violet-500/30 text-violet-200 bg-violet-500/10',
    badgeGlow: 'shadow-[0_0_24px_rgba(139,92,246,0.35)] border-violet-400/40',
    pixel: '#8b5cf6',
  },
};
